/**
 * /admin/reimbursements — accounts-payable view for the cross-redemption
 * network.  Lists every business Hustle currently owes (because
 * customers spent platform Hustle Dollars at their venue), grouped by
 * business with a "Mark Paid" action.
 *
 * Backend: GET /api/business/credits/admin/reimbursements
 *          POST /api/business/credits/reimbursements/{id}/mark-paid
 *          POST /api/business/signup/invite        (generate DM link)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, Banknote, Building2, RefreshCw,
         Send, Copy, X, MessageSquare, Mail, Phone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });

const fmt = (c) => `$${((c || 0) / 100).toLocaleString("en-AU", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;

export default function AdminReimbursementsPage() {
  const [data, setData] = useState({ groups: [], owing_cents: 0, business_count: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("owing");
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState({});
  const [invite, setInvite] = useState(null);          // payload from /invite
  const [inviteBusy, setInviteBusy] = useState(null);  // biz_id while loading

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/business/credits/admin/reimbursements`, {
        params: { status: filter, limit: 500 },
      });
      setData(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markPaid = async (rid) => {
    const ref = prompt("Payout reference (Stripe/Bank/cheque #):", "");
    if (ref === null) return;
    setBusy(b => ({ ...b, [rid]: true }));
    try {
      await api.post(
        `/api/business/credits/reimbursements/${rid}/mark-paid`,
        null,
        { params: { payout_ref: ref || "" } },
      );
      await load();
    } catch (e) {
      alert(`Failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(b => ({ ...b, [rid]: false }));
    }
  };

  const generateInvite = async (group) => {
    setInviteBusy(group.biz_id);
    try {
      const r = await api.post(`/api/business/signup/invite`, {
        biz_id:   group.biz_id,
        biz_name: group.biz_name,
      });
      setInvite(r.data);
    } catch (e) {
      alert(`Couldn't generate link: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setInviteBusy(null);
    }
  };

  return (
    <div style={styles.root} data-testid="admin-reimbursements-root">
      <header style={styles.header}>
        <div>
          <h1 style={styles.h1}>
            <Banknote size={28} style={{ verticalAlign: "-6px", marginRight: 8 }} />
            Reimbursements
          </h1>
          <p style={styles.sub}>
            Cross-redemption accounts payable — Hustle owes these businesses for
            platform Hustle Dollars spent at their venues.
          </p>
        </div>
        <button onClick={load} style={styles.refresh} data-testid="reimb-refresh">
          <RefreshCw size={16} /> Refresh
        </button>
      </header>

      <div style={styles.stats}>
        <div style={styles.stat} data-testid="reimb-total-owing">
          <div style={styles.statLabel}>Total {filter}</div>
          <div style={styles.statValue}>{fmt(data.owing_cents)}</div>
        </div>
        <div style={styles.stat}>
          <div style={styles.statLabel}>Across</div>
          <div style={styles.statValue}>
            {data.business_count} <span style={{ fontSize: 14, color: "#6b7280" }}>businesses</span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={styles.pills}>
          {["owing", "paid", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f === "all" ? "" : f)}
              style={{
                ...styles.pill,
                ...(((filter === f) || (f === "all" && !filter)) ? styles.pillActive : {}),
              }}
              data-testid={`reimb-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={styles.loading} data-testid="reimb-loading">
          <Loader2 size={28} className="spin" /> Loading…
        </div>
      ) : data.groups.length === 0 ? (
        <div style={styles.empty} data-testid="reimb-empty">
          <CheckCircle2 size={36} color="#10b981" />
          <p>Nothing {filter || "to show"} — all square with the network.</p>
        </div>
      ) : (
        <div style={styles.table} data-testid="reimb-table">
          {data.groups.map((g) => (
            <div key={g.biz_id} style={styles.bizRow}>
              <button
                style={styles.bizHeader}
                onClick={() => setExpanded(s => ({ ...s, [g.biz_id]: !s[g.biz_id] }))}
                data-testid={`reimb-biz-${g.biz_id}`}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <Building2 size={20} color="#6b7280" />
                  <div>
                    <div style={styles.bizName}>{g.biz_name}</div>
                    <div style={styles.bizMeta}>{g.count} transactions</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <div style={styles.bizAmount}>{fmt(g.owing_cents)}</div>
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); generateInvite(g); }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation(); generateInvite(g);
                      }
                    }}
                    style={styles.inviteBtn}
                    data-testid={`reimb-invite-${g.biz_id}`}
                    title="Generate Restaurant Agreement + bank-details sign-up link"
                  >
                    {inviteBusy === g.biz_id ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                    Sign-up link
                  </span>
                </div>
              </button>

              {expanded[g.biz_id] && (
                <div style={styles.txList} data-testid={`reimb-biz-tx-${g.biz_id}`}>
                  {g.items.map((it) => (
                    <div key={it.id} style={styles.tx}>
                      <div>
                        <div style={styles.txAmount}>{fmt(it.amount_cents)}</div>
                        <div style={styles.txMeta}>
                          {it.created_at?.slice(0, 16).replace("T", " ")}
                          {" · "}
                          uid {it.uid}
                          {it.bill_ref && ` · bill ${it.bill_ref}`}
                        </div>
                      </div>
                      <div>
                        {it.status === "owing" ? (
                          <button
                            onClick={() => markPaid(it.id)}
                            disabled={busy[it.id]}
                            style={styles.markBtn}
                            data-testid={`reimb-mark-paid-${it.id}`}
                          >
                            {busy[it.id] ? <Loader2 size={14} className="spin" /> : <CheckCircle2 size={14} />}
                            Mark paid
                          </button>
                        ) : (
                          <span style={styles.paidTag}>
                            <CheckCircle2 size={14} /> {it.status}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {invite && <InviteModal invite={invite} onClose={() => setInvite(null)} />}

      <style>{`.spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Sign-up link modal ───────────────────────────────────────────────
function InviteModal({ invite: initialInvite, onClose }) {
  const [invite, setInvite] = useState(initialInvite);
  const [copied, setCopied] = useState(null);
  const [emailTo, setEmailTo] = useState(initialInvite.email || "");
  const [emailing, setEmailing] = useState(false);
  const [emailResult, setEmailResult] = useState(null);

  const copy = (key, text) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 1500);
  };

  const sendEmail = async () => {
    if (!emailTo.trim()) return;
    setEmailing(true);
    setEmailResult(null);
    try {
      const r = await api.post(`/api/business/signup/invite/email`, {
        biz_id:     invite.biz_id,
        biz_name:   invite.biz_name,
        email:      emailTo.trim(),
        owner_name: invite.owner_name,
      });
      // Refresh modal contents from the new invite (fresh token replaces
      // the displayed one, so the admin doesn't accidentally share the
      // old un-emailed link).
      setInvite(r.data);
      setEmailResult(r.data.email_dispatch || { ok: false });
    } catch (e) {
      setEmailResult({ ok: false, reason: e?.response?.data?.detail || e.message });
    } finally {
      setEmailing(false);
    }
  };

  const channels = [
    { key: "dm",       label: "DM / iMessage",  icon: MessageSquare, body: invite.dm_text },
    { key: "sms",      label: "SMS",            icon: Phone,         body: invite.sms_text },
    { key: "whatsapp", label: "WhatsApp",       icon: MessageSquare, body: invite.whatsapp_text },
    {
      key: "email",
      label: "Email (plain text)",
      icon: Mail,
      body: `Subject: ${invite.email_subject}\n\n${invite.email_body_html.replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim()}`,
    },
  ];

  return (
    <div style={modal.overlay} onClick={onClose} data-testid="invite-modal-overlay">
      <div
        style={modal.card}
        onClick={(e) => e.stopPropagation()}
        data-testid="invite-modal"
      >
        <button onClick={onClose} style={modal.close} data-testid="invite-close">
          <X size={18} />
        </button>

        <h2 style={modal.h2}>
          <Send size={20} /> Sign-up link generated
        </h2>
        <p style={modal.sub}>
          One-time link for <strong>{invite.biz_name || invite.biz_id}</strong>.
          Expires in 30 days, single-use. Tap a channel below to copy the
          pre-written message + link, then paste straight into your DM /
          SMS / email client.
        </p>

        <div style={modal.linkBox}>
          <code style={modal.linkText} data-testid="invite-link">{invite.url}</code>
          <button
            onClick={() => copy("link", invite.url)}
            style={modal.copyBtn}
            data-testid="invite-copy-link"
          >
            {copied === "link" ? <CheckCircle2 size={14} /> : <Copy size={14} />}
            {copied === "link" ? "Copied" : "Copy link"}
          </button>
        </div>

        {invite.owing_dollars && (
          <div style={modal.owingBanner}>
            <Banknote size={16} /> Currently owed: <strong>{invite.owing_dollars}</strong>
            {" — referenced in every message body below"}
          </div>
        )}

        {/* ── One-click email send (Resend) ──────────────────────── */}
        <div style={modal.emailBlock} data-testid="invite-email-block">
          <div style={modal.emailHead}>
            <Mail size={16} />
            <strong>One-click email</strong>
            <span style={modal.emailHint}>
              Fires a Resend-styled HTML email with the link + agreement preview embedded
            </span>
          </div>
          <div style={modal.emailRow}>
            <input
              type="email"
              placeholder="owner@venue.com.au"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              style={modal.emailInput}
              data-testid="invite-email-input"
            />
            <button
              onClick={sendEmail}
              disabled={emailing || !emailTo.trim()}
              style={{ ...modal.emailBtn, opacity: (!emailTo.trim() || emailing) ? 0.5 : 1 }}
              data-testid="invite-email-send"
            >
              {emailing
                ? <><Loader2 size={14} className="spin" /> Sending…</>
                : <><Send size={14} /> Send email</>}
            </button>
          </div>
          {emailResult && (
            <div
              style={emailResult.ok ? modal.emailSuccess : modal.emailFail}
              data-testid="invite-email-result"
            >
              {emailResult.ok
                ? <>✓ Email dispatched to <strong>{emailTo}</strong> via Resend. The venue will see the secure link + clauses 7-10 preview.</>
                : <>Couldn&apos;t send: {emailResult.reason || emailResult.message || "unknown"} — use the copy buttons below instead.</>}
            </div>
          )}
        </div>

        <div style={modal.channelDivider}>
          <span>or copy &amp; paste manually</span>
        </div>

        <div style={modal.channelGrid}>
          {channels.map(({ key, label, icon: Icon, body }) => (
            <div key={key} style={modal.channelCard}>
              <div style={modal.channelHead}>
                <Icon size={16} />
                <span>{label}</span>
                <button
                  onClick={() => copy(key, body)}
                  style={modal.copyBtnSmall}
                  data-testid={`invite-copy-${key}`}
                >
                  {copied === key ? <CheckCircle2 size={12} /> : <Copy size={12} />}
                  {copied === key ? "Copied" : "Copy"}
                </button>
              </div>
              <pre style={modal.channelBody}>{body}</pre>
            </div>
          ))}
        </div>

        <div style={modal.footerNote}>
          The venue lands on a single page with the agreement preview
          (clauses 7–10 highlighted), bank-details form (BSB, account
          name, account number) and a sign button. On acceptance, their
          bank details are stamped onto the business record and
          reimbursements auto-release on the 14-day cycle.
        </div>
      </div>
    </div>
  );
}

const styles = {
  root: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px",
          fontFamily: "Inter, system-ui, sans-serif", color: "#0f172a" },
  header: { display: "flex", justifyContent: "space-between",
            alignItems: "start", gap: 24, marginBottom: 24 },
  h1: { fontSize: 30, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" },
  sub: { color: "#475569", fontSize: 14, margin: "8px 0 0", maxWidth: 520 },
  refresh: { display: "inline-flex", alignItems: "center", gap: 8,
             border: "1px solid #e2e8f0", background: "white",
             padding: "8px 14px", borderRadius: 8, cursor: "pointer",
             fontWeight: 600, color: "#475569" },
  stats: { display: "flex", alignItems: "center", gap: 16,
           marginBottom: 28, padding: 20, background: "#f8fafc",
           borderRadius: 14, border: "1px solid #e2e8f0" },
  stat: { display: "flex", flexDirection: "column", gap: 4 },
  statLabel: { fontSize: 11, textTransform: "uppercase",
               letterSpacing: "0.1em", color: "#64748b", fontWeight: 700 },
  statValue: { fontSize: 26, fontWeight: 800, color: "#0f172a" },
  pills: { display: "flex", gap: 6, background: "white",
           padding: 4, borderRadius: 999, border: "1px solid #e2e8f0" },
  pill: { background: "transparent", border: "none", padding: "6px 14px",
          borderRadius: 999, cursor: "pointer", fontWeight: 600,
          textTransform: "capitalize", color: "#64748b", fontSize: 13 },
  pillActive: { background: "#0f172a", color: "white" },
  loading: { display: "flex", alignItems: "center", gap: 12, padding: 40,
             color: "#64748b", justifyContent: "center" },
  empty: { display: "flex", flexDirection: "column", alignItems: "center",
           gap: 12, padding: 60, color: "#64748b" },
  table: { display: "flex", flexDirection: "column", gap: 12 },
  bizRow: { background: "white", border: "1px solid #e2e8f0",
            borderRadius: 12, overflow: "hidden" },
  bizHeader: { width: "100%", display: "flex", justifyContent: "space-between",
               alignItems: "center", padding: "18px 20px", background: "white",
               border: "none", cursor: "pointer", textAlign: "left" },
  bizName: { fontSize: 16, fontWeight: 700, color: "#0f172a" },
  bizMeta: { fontSize: 12, color: "#64748b", marginTop: 2 },
  bizAmount: { fontSize: 20, fontWeight: 800, color: "#0f172a",
               fontVariantNumeric: "tabular-nums" },
  txList: { borderTop: "1px solid #e2e8f0", background: "#fafbfc" },
  tx: { display: "flex", justifyContent: "space-between",
        alignItems: "center", padding: "14px 24px",
        borderBottom: "1px solid #e2e8f0" },
  txAmount: { fontWeight: 700, color: "#0f172a" },
  txMeta: { fontSize: 12, color: "#64748b", marginTop: 2 },
  markBtn: { display: "inline-flex", alignItems: "center", gap: 6,
             background: "#0f172a", color: "white", border: "none",
             padding: "8px 14px", borderRadius: 8, fontWeight: 600,
             cursor: "pointer", fontSize: 13 },
  paidTag: { display: "inline-flex", alignItems: "center", gap: 6,
             color: "#10b981", fontWeight: 600, fontSize: 13,
             textTransform: "capitalize" },
  inviteBtn: { display: "inline-flex", alignItems: "center", gap: 6,
               background: "#fbbf24", color: "#0f172a", border: "none",
               padding: "8px 14px", borderRadius: 999, fontWeight: 700,
               cursor: "pointer", fontSize: 12 },
};

const modal = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)",
             display: "flex", alignItems: "center", justifyContent: "center",
             padding: 24, zIndex: 100, backdropFilter: "blur(4px)" },
  card: { background: "white", borderRadius: 16, padding: 32,
          maxWidth: 720, width: "100%", maxHeight: "90vh",
          overflowY: "auto", position: "relative",
          boxShadow: "0 28px 80px rgba(0,0,0,0.4)" },
  close: { position: "absolute", top: 18, right: 18, background: "#f1f5f9",
           border: "none", width: 32, height: 32, borderRadius: "50%",
           cursor: "pointer", display: "inline-flex", alignItems: "center",
           justifyContent: "center" },
  h2: { display: "flex", alignItems: "center", gap: 8, margin: "0 0 8px",
        fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" },
  sub: { color: "#475569", fontSize: 14, lineHeight: 1.55,
         margin: "0 0 24px" },
  linkBox: { display: "flex", alignItems: "center", gap: 8,
             background: "#f8fafc", border: "1px solid #e2e8f0",
             borderRadius: 10, padding: "10px 14px", marginBottom: 16 },
  linkText: { flex: 1, fontSize: 13, color: "#0f172a",
              wordBreak: "break-all", fontFamily: "monospace" },
  copyBtn: { display: "inline-flex", alignItems: "center", gap: 6,
             background: "#0f172a", color: "white", border: "none",
             padding: "8px 12px", borderRadius: 8, fontWeight: 600,
             cursor: "pointer", fontSize: 12, flexShrink: 0 },
  copyBtnSmall: { display: "inline-flex", alignItems: "center", gap: 4,
                  background: "transparent", color: "#0ea5e9",
                  border: "1px solid #0ea5e9", padding: "4px 10px",
                  borderRadius: 999, fontWeight: 700, cursor: "pointer",
                  fontSize: 11, marginLeft: "auto" },
  owingBanner: { background: "#fef3c7", color: "#92400e",
                 padding: "10px 14px", borderRadius: 10, fontSize: 13,
                 marginBottom: 24, display: "flex", alignItems: "center",
                 gap: 8 },
  channelGrid: { display: "grid", gridTemplateColumns: "1fr 1fr",
                 gap: 12, marginBottom: 20 },
  channelCard: { background: "#f8fafc", border: "1px solid #e2e8f0",
                 borderRadius: 10, padding: 14 },
  channelHead: { display: "flex", alignItems: "center", gap: 8,
                 marginBottom: 8, fontSize: 12, fontWeight: 700,
                 color: "#0f172a", textTransform: "uppercase",
                 letterSpacing: "0.06em" },
  channelBody: { fontSize: 12, color: "#475569", margin: 0,
                 whiteSpace: "pre-wrap", lineHeight: 1.5,
                 maxHeight: 140, overflowY: "auto",
                 fontFamily: "ui-monospace, SFMono-Regular, monospace" },
  footerNote: { background: "#f0f9ff", border: "1px solid #bae6fd",
                color: "#075985", padding: "12px 16px", borderRadius: 10,
                fontSize: 13, lineHeight: 1.55 },
  emailBlock: { background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
                color: "white", borderRadius: 12, padding: 18,
                marginBottom: 20 },
  emailHead: { display: "flex", alignItems: "center", gap: 8,
               marginBottom: 12, fontSize: 13, color: "#fbbf24" },
  emailHint: { color: "#94a3b8", fontWeight: 400, fontSize: 12,
               marginLeft: "auto" },
  emailRow: { display: "flex", gap: 8 },
  emailInput: { flex: 1, padding: "10px 14px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8,
                color: "white", fontSize: 14, outline: "none",
                fontFamily: "inherit" },
  emailBtn: { display: "inline-flex", alignItems: "center", gap: 6,
              background: "#fbbf24", color: "#0f172a", border: "none",
              padding: "10px 18px", borderRadius: 8, fontWeight: 700,
              cursor: "pointer", fontSize: 13 },
  emailSuccess: { marginTop: 12, padding: "10px 14px",
                  background: "rgba(34,197,94,0.15)",
                  border: "1px solid rgba(34,197,94,0.4)",
                  color: "#86efac", borderRadius: 8, fontSize: 13 },
  emailFail: { marginTop: 12, padding: "10px 14px",
               background: "rgba(239,68,68,0.15)",
               border: "1px solid rgba(239,68,68,0.4)",
               color: "#fca5a5", borderRadius: 8, fontSize: 13 },
  channelDivider: { textAlign: "center", margin: "8px 0 16px",
                    position: "relative", color: "#94a3b8",
                    fontSize: 11, textTransform: "uppercase",
                    letterSpacing: "0.12em", fontWeight: 700 },
};

